import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { getColorTheme } from '@aytm/app/stats/lib/stat-page'
import { calcGradientColor, COLOR_GRADIENTS } from '@aytm/app/stats/lib/render/colors'

import {
  calcChartSelectOptions,
  calcChartSelectOptionsWithNone,
  calcDefaultMode,
  calcModes,
  calcProcessedData
} from '@aytm/app/stats/page/question-views/bpm/lib/model-data'

import useBpmState from '@aytm/app/stats/page/question-views/bpm/states/useBpmState'

import ModeSwitcher from '@aytm/app/stats/page/question-views/bpm/components/ModeSwitcher'
import ModeSection from '@aytm/app/stats/page/question-views/bpm/components/ModeSection'

import DataRun from '@aytm/app/stats/page/question-views/components/data-run/DataRun'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

// Everything the researcher sees once the question has numbers: the sub-mode's own controls, chart
// and table, fronted by the switcher when there is more than one mode to offer.
const BlockBody = ({
  state,
  questionVariants,
  batches,
  showResponses,
  quadrantChartSelectOptions,
  quadrantChartSelectOptionsWithNone,
  modes,
  ...props
}) => {
  const {
    id,
    questionKey,
    answer,
    components,
    defaultPlotByValue,
    scores,
    crossing,
    plots,
    title,
    dataLabels = 'hover',
    xAxisLimits,
    yAxisLimits,
    chartMode,
    chartLegendMode,
    features,
    onChartReady
  } = props

  const { mode, onModelChange } = state

  // everything the sub-mode's controls, chart and table draw from
  const view = {
    ...state,
    id,
    questionKey,
    questionVariants,
    batches,
    answer,
    showResponses,
    defaultPlotByValue,
    scores,
    crossing,
    plots,
    title,
    dataLabels,
    xAxisLimits,
    yAxisLimits,
    quadrantChartSelectOptions,
    quadrantChartSelectOptionsWithNone,
    colorTheme: getColorTheme(),
    colorGradient: calcGradientColor,
    colorGradients: COLOR_GRADIENTS,
    chartMode,
    chartLegendMode,
    features,
    onChartReady
  }

  return (
    <ModeSection
      {...view}
      components={components}
      switcher={1 < modes.length ? <ModeSwitcher mode={mode} onModelChange={onModelChange} /> : null}
    />
  )
}

BlockBody.displayName = 'BpmBlockBody'

// Composite BPM block: mode switcher + the sub-mode's own controls, chart and table. Replaces the
// per-mode Backbone view trees (quadrant/tv/mds). block.coffee owns the Backbone model and report
// params; this component only renders resolved values and reports intents back through the api it
// hands up on mount (heatmap ownership pattern).
const Block = props => {
  const {
    questionVariants: questionVariantsProp,
    batches: batchesProp,
    answer,
    type,
    calcBadData,
    preprocessed = false,
    qLogicVisibility,
    qAliasesVisibility,
    questionKey,
    schemaKey,
    defaultPlotByValue,
    params,
    paramsRevision,
    modes: modesProp,
    defaultMode: defaultModeProp,
    onReady,
    onStateChange,
    onBadData
  } = props

  const { questionVariants, batches, showResponses } = useMemo(
    () =>
      calcProcessedData({
        questionVariants: questionVariantsProp,
        batches: batchesProp,
        preprocessed,
        qLogicVisibility,
        qAliasesVisibility
      }),
    [questionVariantsProp, batchesProp, preprocessed, qLogicVisibility, qAliasesVisibility]
  )

  const modes = useMemo(() => calcModes({ modes: modesProp, type }), [modesProp, type])
  const defaultMode = calcDefaultMode({ defaultMode: defaultModeProp, modes: modesProp, type })

  const quadrantChartSelectOptions = useMemo(() => calcChartSelectOptions(batches), [batches])
  const quadrantChartSelectOptionsWithNone = useMemo(() => calcChartSelectOptionsWithNone(batches), [batches])

  // the api the block hands up is owed on mount, so the state lives here rather than in the body
  const state = useBpmState({
    params,
    paramsRevision,
    modes,
    defaultMode,
    defaultPlotByValue,
    questionVariants,
    batches,
    selectOptions: quadrantChartSelectOptions,
    selectOptionsWithNone: quadrantChartSelectOptionsWithNone,
    onReady,
    onStateChange
  })

  // the question's own rule, applied to the payload on screen -- read here rather than taken as a
  // flag, so what the block draws can never disagree with what it was handed
  const badData = Boolean(calcBadData?.(answer))

  return (
    <BlockWrapper answer={answer}>
      <DataRun
        answer={answer}
        badData={badData}
        type={type}
        questionKey={questionKey}
        schemaKey={schemaKey}
        calcBadData={calcBadData}
        onBadData={onBadData}
        colorTheme={getColorTheme()}
      />
      {/* not keyed on the mode: remounting the section puts the incoming chart back behind an
          on-screen report the switch itself can push out of view */}
      {badData || !state.mode ? null : (
        <BlockBody
          {...props}
          state={state}
          questionVariants={questionVariants}
          batches={batches}
          showResponses={showResponses}
          quadrantChartSelectOptions={quadrantChartSelectOptions}
          quadrantChartSelectOptionsWithNone={quadrantChartSelectOptionsWithNone}
          modes={modes}
        />
      )}
    </BlockWrapper>
  )
}

Block.displayName = 'BpmBlock'

Block.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  questionKey: PropTypes.string,
  questionVariants: PropTypes.array.isRequired,
  batches: PropTypes.array.isRequired,
  answer: answerPropType,
  type: PropTypes.string,
  calcBadData: PropTypes.func,
  preprocessed: PropTypes.bool,
  qLogicVisibility: PropTypes.string,
  qAliasesVisibility: PropTypes.string,
  schemaKey: PropTypes.string,
  components: PropTypes.object,
  defaultPlotByValue: PropTypes.string,
  scores: PropTypes.array,
  crossing: PropTypes.object,
  plots: PropTypes.array,
  title: PropTypes.string,
  dataLabels: PropTypes.string,
  xAxisLimits: PropTypes.object,
  yAxisLimits: PropTypes.object,
  params: PropTypes.shape({ bpm: PropTypes.string, s: PropTypes.string }),
  paramsRevision: PropTypes.number,
  modes: PropTypes.arrayOf(PropTypes.string),
  defaultMode: PropTypes.string,
  chartMode: PropTypes.string,
  chartLegendMode: PropTypes.string,
  features: PropTypes.object,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onBadData: PropTypes.func,
  onChartReady: PropTypes.func
}

// the body is handed the block's own props, plus the state and the resolved values the block owns
BlockBody.propTypes = {
  ...Block.propTypes,
  state: PropTypes.object,
  showResponses: PropTypes.bool,
  quadrantChartSelectOptions: PropTypes.array,
  quadrantChartSelectOptionsWithNone: PropTypes.array
}

export default Block
